import React, { useState } from "react";
import { useDispatch } from "react-redux";

/* UI */
import { Button, Separator } from "@lemonadetraining/web-ui";
import { IconRowInsertBottom, IconRowRemove } from "@tabler/icons-react";

/* Own Modules */
import { TimelineWeek } from "../../Common/Timeline";
import { notifyActions, NOTIFICATION_TYPE } from "@lemonadetraining/web-shared";

/*
 * ======================================================
 * Timeline Control Component
 * ======================================================
 */
interface Props {
  numberOfWeeks: number | null;
  isItMyProgramme: boolean;
  onNumberOfWeekChanged?: (theWeek: TimelineWeek) => void;
  onWeekSelected?: (theWeek: TimelineWeek) => void;
}

const TimelineControl: React.FC<Props> = ({
  numberOfWeeks,
  isItMyProgramme = false,
  onNumberOfWeekChanged,
  onWeekSelected,
}) => {
  const dispatch = useDispatch();

  // ------- state initialization -------
  const weekUseUseStateInit = () => {
    // if numberOfWeeks is not defined, then return empty array
    if (!numberOfWeeks) {
      const newWeek: TimelineWeek = { number: 1, selected: true };
      if (onWeekSelected) onWeekSelected(newWeek);
      if (onNumberOfWeekChanged) onNumberOfWeekChanged(newWeek);
      return [newWeek];
    }
    // if weeks is defined, then return the weeks
    const tempWeeks: TimelineWeek[] = [];
    for (let i = 1; i <= numberOfWeeks; i++) {
      const newWeek: TimelineWeek = { number: i, selected: false };
      if (i === 1) {
        newWeek.selected = true;
        if (onWeekSelected) onWeekSelected(newWeek);
      }
      tempWeeks.push(newWeek);
    }
    return tempWeeks;
  };

  const [weeks, setWeeks] = useState<TimelineWeek[]>(() => weekUseUseStateInit());
  // ------- helpers -------
  const addNewWeek = () => {
    // if number of weeks is more than 12, then return
    if (weeks.length >= 12) {
      dispatch(
        notifyActions.sendNotification({
          type: NOTIFICATION_TYPE.warning,
          message: "Maximum 12 weeks are allowed",
        })
      );
      return;
    }

    const newWeek: TimelineWeek = { number: weeks.length + 1, selected: false };

    // passing data to onNumberOfWeekChanged
    if (onNumberOfWeekChanged) onNumberOfWeekChanged(newWeek);

    setWeeks((prev) => [...prev, newWeek]);
  };

  const selectedWeek = (week: TimelineWeek) => {
    // passing data to onWeekSelected
    if (onWeekSelected) onWeekSelected(week);

    // update the state
    setWeeks((prev) => {
      return prev.map((w) => {
        if (w.number === week.number) {
          w.selected = true;
        } else {
          w.selected = false;
        }
        return w;
      });
    });
  };

  const removeLastWeek = () => {
    // ----- pre-check
    if (weeks.length === 0 || weeks.length === 1) {
      dispatch(
        notifyActions.sendNotification({
          type: NOTIFICATION_TYPE.warning,
          message: "No weeks to remove",
        })
      );
      return;
    }
    // --------------

    if (onNumberOfWeekChanged) {
      // weeks.length - 1 ==> is the week will be removed
      // weeks.length - 2 ==> is the last week that is present
      const lastWeek = weeks[weeks.length - 2];
      onNumberOfWeekChanged(lastWeek);
    }

    setWeeks((prev) => prev.slice(0, -1));
  };

  /* ====================[UI Return]===================== */
  return (
    <>
      <h4 className="text-base font-medium">Timeline In Weeks</h4>
      <div className="flex flex-row items-center">
        <div className="flex flex-1 items-center gap-0">
          {weeks.length > 0 &&
            weeks.map((week, index) => (
              <button
                key={index}
                onClick={() => selectedWeek(week)}
                className={`flex size-8 items-center justify-center rounded-full border text-sm transition-colors ${
                  week.selected
                    ? "border-primary bg-primary/10 text-primary font-medium"
                    : "border-border text-muted-foreground hover:bg-muted"
                }`}
              >
                {index + 1}
              </button>
            ))}
        </div>
        {isItMyProgramme && (
          <div className="ml-2 flex flex-row gap-2">
            <Button variant="outline" onClick={addNewWeek}>
              <IconRowInsertBottom className="mr-1 size-4" />
              Week
            </Button>
            <Button variant="destructive" onClick={removeLastWeek}>
              <IconRowRemove className="mr-1 size-4" />
              Week
            </Button>
          </div>
        )}
      </div>
      <Separator className="my-2" />
    </>
  );
};

export default TimelineControl;
